import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useDrag } from 'react-dnd';
import i18n from 'i18n-js';
import clsx from 'clsx';

import { Button, Grid, IconButton } from '@material-ui/core';
import { NavigateNext } from '@material-ui/icons';
import PrintIcon from '@material-ui/icons/Print';

import { images } from 'assets';
import { ModifiedIngredient, OrderItem } from 'store/ordersStore/reducer';
import { StyledChip, Text } from 'components/shared';
import { formatIngredientWeight, removeWhiteSpace } from 'helpers/helperFunctions';
import { useStyles } from '../styles';
import { MenuType, OrderItemStatus } from 'constants/enums';
import { ApplicationState } from 'store';
import { CookIngredientsList } from './IngridientsList';
import { useAppSelector } from 'index';
import { RGB } from 'helpers/colorHelper';
import { theme } from 'config/theme';

interface SectionCardProps {
    orderItem: OrderItem;
    passive?: boolean;
    changeOrderStatus(item: OrderItem, status: OrderItemStatus): void;
    onPrintClick(event: React.MouseEvent<HTMLButtonElement>, item: OrderItem): void;
}

export default function SectionCard({
    orderItem,
    passive,
    changeOrderStatus,
    onPrintClick,
}: SectionCardProps) {
    const {
        amount,
        comment,
        service,
        name,
        orderNumber,
        qrCodeName,
        tableName,
        size,
        status,
        categoryId,
        modifiedIngredients,
        menuType,
        courseName,
        courseNumber,
    } = orderItem;
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const orderComment = useSelector(
        (state: ApplicationState) =>
            state.orders.orders.find((o) => o.id === orderItem.orderId)?.comment
    );
    const category = useSelector(
        (state: ApplicationState) => state.category.categoryDetails?.categories
    )?.find((category) => category.id === categoryId);
    const categoryName = category?.name;
    const categoryColor = category?.color;

    const classes = useStyles();
    const [isScrolling, setIsScrolling] = useState(false);
    const isItemStatusChanging = useSelector(
        (state: ApplicationState) => state.orders.isItemStatusChanging
    );

    const options = useMemo(() => {
        return { dropEffect: 'move' };
    }, []);

    const [{ isDragging }, dragRef] = useDrag(
        () => ({
            type: 'a',
            item: orderItem,
            canDrag: () => !isScrolling,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            options,
        }),
        []
    );

    const handleTouchStart = () => setIsScrolling(false);
    const handleTouchMove = () => setIsScrolling(true);

    function getServiceIcon() {
        switch (service) {
            case 'HomeDelivery':
                return images.icons.chartHomeDelivery;
            case 'TakeAway':
                return images.icons.chartTakeAway;
            case 'DineIn':
                return images.icons.chartDineIn;
            case 'TakeOut':
                return images.icons.chartDineIn;
            default:
                return images.icons.takeAway;
        }
    }

    function onNextStatus() {
        if (isItemStatusChanging) return;
        switch (status) {
            case OrderItemStatus.Order:
            case OrderItemStatus.ReadyToPrepare:
                changeOrderStatus(orderItem, OrderItemStatus.InProgress);
                break;
            case OrderItemStatus.InProgress:
                changeOrderStatus(orderItem, OrderItemStatus.Ready);
                break;
        }
    }

    function prepareModifiedIngredients() {
        if (!modifiedIngredients || modifiedIngredients.length === 0) return <></>;

        const toLabel = (ingredient: ModifiedIngredient) => {
            const weight = formatIngredientWeight(ingredient.amount, ingredient.unitOfMeasure);
            return weight ? `${weight} ${ingredient.name}` : ingredient.name;
        };

        const additions = modifiedIngredients
            .filter((ingredient) => ingredient.isAdditionChecked && ingredient.name)
            .map(toLabel);
        const removals = modifiedIngredients
            .filter((ingredient) => ingredient.isRemovalChecked && ingredient.name)
            .map(toLabel);

        return (
            <div className={classes.sectionIgriredientsContainer}>
                {additions.length > 0 && CookIngredientsList('add', additions)}
                {removals.length > 0 && CookIngredientsList('remove', removals)}
            </div>
        );
    }

    function renderCourseElement() {
        if (menuType !== MenuType.MultiCourse && !courseNumber) {
            return;
        }
        const label = courseNumber
            ? `${i18n.t('common.course')} ${courseNumber}`
            : courseName ?? '';

        return (
            <Grid
                container
                spacing={1}
                justifyContent="flex-end"
                alignItems="center"
                className={classes.mTop10}
            >
                <StyledChip
                    label={label}
                    labelFontWeight="bold"
                    style={{ backgroundColor: theme.palette.warning.main, maxWidth: '100%' }}
                />
            </Grid>
        );
    }

    return (
        <div
            ref={dragRef}
            className={`${classes.sectionCardContainer} ${
                (passive || isDragging) && classes.passiveContainer
            }`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            style={{
                backgroundColor:
                    (menuType === MenuType.MultiCourse &&
                        status === OrderItemStatus.ReadyToPrepare) ||
                    (courseNumber && status === OrderItemStatus.ReadyToPrepare)
                        ? theme.palette.success.main
                        : undefined,
            }}
        >
            <div className={classes.sectionCardInnerContainer}>
                <div className={classes.flexRowSpaceBetween}>
                    <div>
                        <span className={classes.sectionCardTitle}>{name}</span>
                        {restaurant.advancedSettings?.isPrintingEnabled && (
                            <IconButton
                                style={{ marginLeft: 10 }}
                                onClick={(e) => onPrintClick(e, orderItem)}
                            >
                                <PrintIcon />
                            </IconButton>
                        )}
                    </div>

                    <div /* style={{ maxWidth: '33%' }} */ className={classes.flexRow}>
                        <img
                            style={{
                                height: 22,
                            }}
                            src={getServiceIcon()}
                            alt="section-icon"
                            className={classes.mRight10}
                        />
                        <Text
                            fontSize={18}
                            text={`№ ${orderNumber}`}
                            minifyThreshold={2}
                            className={classes.sectionCardOrder}
                        />
                    </div>
                </div>
                {renderCourseElement()}
                {prepareModifiedIngredients()}
                <div
                    className={clsx(
                        classes.alignCenter,
                        classes.flexRowSpaceBetweenm,
                        classes.mTop10,
                        classes.sectionCardBottomContainer
                    )}
                >
                    <div className={clsx(classes.qrNameText)}>
                        {removeWhiteSpace(tableName ?? qrCodeName)}
                    </div>
                    <div className={clsx(classes.flexRowEnd)}>
                        {categoryName && (
                            <div
                                className={clsx(classes.dishSizeCook, classes.mLeft10)}
                                style={RGB.getStyleForBackground(categoryColor || '')}
                            >
                                {categoryName}
                            </div>
                        )}
                        {size && (
                            <div className={clsx(classes.dishSizeCook, classes.mLeft10)}>
                                {size}
                            </div>
                        )}
                        {amount > 1 && (
                            <div
                                className={clsx(
                                    classes.sectionCardAmountContainer,
                                    classes.mLeft10
                                )}
                            >
                                <span className={classes.sectionCardAmountContainerText}>
                                    {amount}
                                </span>
                            </div>
                        )}
                        {!passive && (
                            <Button
                                variant="contained"
                                size="small"
                                className={clsx(classes.mediumIcon, classes.mLeft10)}
                                onClick={() => onNextStatus()}
                                style={{
                                    minWidth: 'unset',
                                }}
                            >
                                <NavigateNext />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            {orderComment && (
                <div className={classes.sectionCardClientNotesContainer}>
                    <span className={classes.sectionCardClientNotesTitle}>
                        {i18n.t('common.orderNotes')}
                    </span>
                    <span className={classes.sectionCardClientNotesText}>{orderComment}</span>
                </div>
            )}
            {comment && (
                <div className={classes.sectionCardClientNotesContainer}>
                    <span className={classes.sectionCardClientNotesTitle}>
                        {i18n.t('common.itemNotes')}
                    </span>
                    <span className={classes.sectionCardClientNotesText}>{comment}</span>
                </div>
            )}
        </div>
    );
}
